import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { Icon } from '@common/general-ui/icon/icon.component';
import { Link } from '@common/general-ui/link/link.component';
import { ILinkItem } from '@common/utils/make-links/make-links.util';
import { SerializedStyles } from '@emotion/react';
import { Button, Tag as AntdTag, TagProps } from 'antd';
import { PresetStatusColorType } from 'antd/lib/_util/colors';
import React, { ReactNode } from 'react';
import { css } from 'twin.macro';
export type TStatuses = 'default' | 'error' | 'warning' | 'info' | 'success' | 'success-light';

export interface IStatusTagProps extends Omit<TagProps, 'color' | 'title'> {
    name: string;
    status: TStatuses | undefined;
    className?: string;
    hideIcon?: boolean;
    title?: ReactNode;
    children?: ReactNode;
    showClose?: boolean;
    showButton?: boolean;
    buttonText?: string;
    onButtonClick?: () => void;
    onCloseIconClick?: () => void;
    href?: ILinkItem;
    customIcon?: ReactNode;
    addBottomMargin?: boolean;
    matchTextColor?: boolean;
}

interface ITagConfig {
    icon: ReactNode;
    bgColor: TagProps['color'];
    borderColor: TagProps['color'];
    buttonTextColor: TagProps['color'];
}

const TAG_CONFIG_MAP: Record<TStatuses, ITagConfig> = {
    success: {
        bgColor: '#F1F9F4',
        borderColor: '#2E5A36',
        buttonTextColor: '#ffffff',
        icon: (
            <div className="left-icon">
                <Icon icon={CheckCircleOutlined} />
            </div>
        ),
    },
    ['success-light']: {
        bgColor: '#F1F9F4',
        borderColor: '#44AA61',
        buttonTextColor: '#ffffff',
        icon: (
            <div className="left-icon">
                <Icon icon={CheckCircleOutlined} />
            </div>
        ),
    },
    info: {
        bgColor: '#EFEEFF',
        borderColor: '#4C498C',
        buttonTextColor: '#ffffff',
        icon: (
            <div className="left-icon">
                <Icon icon={InfoCircleOutlined} />
            </div>
        ),
    },
    error: {
        bgColor: '#FDEEEC',
        borderColor: '#D00443',
        buttonTextColor: '#ffffff',
        icon: (
            <div className="left-icon">
                <Icon icon={ExclamationCircleOutlined} />
            </div>
        ),
    },
    default: {
        bgColor: '#EEEEEE',
        borderColor: '#585858',
        buttonTextColor: '#ffffff',
        icon: (
            <div className="left-icon">
                <Icon icon={InfoCircleOutlined} />
            </div>
        ),
    },
    warning: {
        bgColor: '#FEF7EA',
        borderColor: '#F2B63E',
        buttonTextColor: '#000000',
        icon: (
            <div className="left-icon">
                <Icon icon={WarningOutlined} />
            </div>
        ),
    },
};

export const StatusTagV2 = ({
    name,
    showButton,
    onButtonClick,
    onCloseIconClick,
    showClose,
    buttonText,
    status = 'default',
    hideIcon,
    customIcon,
    addBottomMargin,
    title,
    matchTextColor,
    href,
    ...rest
}: IStatusTagProps): JSX.Element => {
    const config = TAG_CONFIG_MAP[status as PresetStatusColorType] as ITagConfig | undefined;
    const icon = status && config ? config.icon : undefined;
    return (
        <AntdTag
            data-status={status}
            data-testid={`status-tag-${name}`}
            css={
                styles.root(
                    config?.bgColor as string,
                    config?.borderColor as string,
                    showClose,
                    addBottomMargin,
                    matchTextColor,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ) as any
            }
            icon={hideIcon ? undefined : customIcon ? <div className="left-icon">{customIcon}</div> : icon}
            {...rest}
            color={'black'}
            className={rest.className}
        >
            <div data-testid="title" css={styles.title}>
                {title}
            </div>
            {rest.children && (
                <div data-testid="description" css={styles.children}>
                    {rest.children}
                </div>
            )}
            {showButton && (
                <Button
                    onClick={onButtonClick}
                    data-testid="button"
                    css={styles.button(config?.buttonTextColor, config?.borderColor)}
                >
                        <Link name={`link-${name}`} href={href} noStyle={true} tw="flex">
                            {buttonText}
                        </Link>
                </Button>
            )}
            {showClose && (
                <CloseCircleOutlined
                    onClick={(): void => onCloseIconClick && onCloseIconClick()}
                    data-testid="close"
                    css={styles.closeIcon(config?.borderColor, matchTextColor)}
                />
            )}
        </AntdTag>
    );
};

const styles = {
    root: (
        color: string,
        borderColor: string,
        showClose: boolean | undefined,
        addBottomMargin: boolean | undefined,
        matchTextColor: boolean | undefined,
    ): (SerializedStyles | boolean | undefined)[] => [
        css`
            width: 100%;
            margin: 0px;
            box-sizing: border-box;
            white-space: normal;
            position: relative;
            display: flex;
            flex-direction: row;
            padding: 11px;
            gap: 11px;
            background-color: ${color}!important;
            color: ${matchTextColor ? borderColor : 'black'} !important;
            border: 1px solid ${borderColor}!important;
            border-radius: 6px;
            .left-icon {
                svg {
                    margin-top: -2px;
                    font-size: 18px;
                    fill: ${borderColor}!important;
                }
            }

            && span {
                margin-left: 0px;
            }
        `,
        showClose &&
            css`
                padding-right: 38px;
            `,
        addBottomMargin &&
            css`
                margin-bottom: 16px;
            `,
    ],
    button: (buttonTextColor: string | undefined, borderColor: string | undefined): SerializedStyles | boolean => css`
        color: ${buttonTextColor};
        padding: 6px 12px;
        border-radius: 4px;
        border: 0px;
        background-color: ${borderColor}!important;
        font-weight: 400;
        font-size: 16px;
        line-height: 20px;
        margin-top: 8px;
        min-width: 75px;

        :hover {
            opacity: 0.75;
        }
    `,
    closeIcon: (borderColor: string | undefined, matchTextColor: boolean | undefined): SerializedStyles | boolean => css`
        font-size: 14px;
        color: ${matchTextColor ? borderColor : 'black'} !important;
        position: absolute;
        right: 0%;
        margin: 0px 12px 0px 12px !important;
        top: 50%;
        transform: translate(0, -50%);
    `,
    title: css`
        white-space: normal;
        font-size: 16px;
        line-height: 24px;
        font-weight: 400;
    `,
    children: css`
        white-space: normal;
        font-size: 14px;
        line-height: 24px;
        font-weight: 300;
    `,
};
